import "react-calendar/dist/Calendar.css";
import DataTable, { TableColumn } from "react-data-table-component";
import { toast } from "react-toastify";
import { setupAPIclient } from "../../services/setupAPIclient";
import LocalStorage from "../../helpers/localstorage";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";
import { useState, useEffect, FormEvent } from "react";
import { motion } from "framer-motion";
import {
  FiTrash2,
  FiPlusCircle,
  FiCheckCircle,
  FiSearch,
  FiBox,
  FiHash,
  FiEdit,
  FiList,
  FiX,
} from "react-icons/fi";
import LayoutSubmasterToUser from "../../components/layoutConfig/layoutSubmasterToUser";

interface FormRegionInput {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

function RegionFormSubmaster() {
  const localStorage = new LocalStorage();
  const token = String(localStorage.getAuthTokenUserToSubmaster());
  const [data, setData] = useState<FormRegionInput[]>([]);
  const [filterText, setFilterText] = useState("");
  const [pending, setPending] = useState(true);
  const [editId, setEditId] = useState<number | null>(null);
  const [formRegiao, setFormRegiao] = useState({ name: "" });

  const ApiClient = setupAPIclient(token);

  const handleGetDataInput = async () => {
    try {
      setPending(true);
      const response = await ApiClient.post<FormRegionInput[]>("/management/get/region");
      setData(response.data || []);
    } catch (error) {
      console.error("Erro ao buscar dados:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setPending(false);
    }
  };

  const handleCreateOrUpdate = async (e: FormEvent) => {
    e.preventDefault();

    if (!formRegiao.name.trim()) {
      toast.warn("Preencha o nome da região");
      return;
    }

    try {
      if (editId) {
        await ApiClient.post("/management/update/region", {
          id: editId,
          name: formRegiao.name,
        });
        toast.success("Região atualizada com sucesso");
      } else {
        await ApiClient.post("/management/create/region", { ...formRegiao });
        toast.success("Região cadastrada com sucesso");
      }

      setEditId(null);
      setFormRegiao({ name: "" });
      handleGetDataInput();
    } catch (error) {
      console.error("Erro ao salvar:", error);
      toast.error("Erro ao salvar região");
    }
  };

  const handleEdit = (row: FormRegionInput) => {
    setEditId(row.id);
    setFormRegiao({ name: row.name || "" });
  };

  const handleCancelEdit = () => {
    setEditId(null);
    setFormRegiao({ name: "" });
  };

  const handleDelete = async (row: FormRegionInput) => {
    if (!window.confirm(`Tem certeza que deseja deletar a região ${row.name}?`)) return;
    try {
      const response = await ApiClient.post("/management/delete/region", { id: row.id });
      if (!response.data.success) {
        toast.warning(`Não é possível deletar. Existem dados associados à região ${row.name}`);
      } else {
        toast.success("Registro deletado com sucesso");
        if (editId === row.id) {
          handleCancelEdit();
        }
        handleGetDataInput();
      }
    } catch (error) {
      console.error("Erro ao deletar:", error);
      toast.warning(`Não é possível deletar. Existem dados associados à região ${row.name}`);
    }
  };

  useEffect(() => {
    handleGetDataInput();
  }, []);

  const iconColor = "#5dade2";

  const columns: TableColumn<FormRegionInput>[] = [
    {
      name: (
        <div className="d-flex align-items-center gap-1">
          <FiHash color={iconColor} size={18} /> ID
        </div>
      ),
      selector: (row) => row.id,
      sortable: true,
      minWidth: "80px",
    },
    {
      name: (
        <div className="d-flex align-items-center gap-1">
          <FiBox color={iconColor} size={18} /> Nome
        </div>
      ),
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2 fw-semibold">
          <FiBox color={iconColor} /> {row.name}
        </div>
      ),
      minWidth: "200px",
    },
    {
      name: "Ações",
      cell: (row) => (
        <div className="d-flex gap-2 justify-content-center flex-wrap">
          <motion.button
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.95 }}
            className="btn btn-sm modern-primary-btn"
            onClick={() => handleEdit(row)}
          >
            <FiEdit />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.95 }}
            className="btn btn-sm btn-outline-danger"
            onClick={() => handleDelete(row)}
          >
            <FiTrash2 />
          </motion.button>
        </div>
      ),
      center: true,
      minWidth: "140px",
    },
  ];

  const filteredItems = data.filter((item) => item.name?.toLowerCase().includes(filterText.toLowerCase()));

  return (
    <LayoutSubmasterToUser>
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="d-flex flex-wrap gap-3 mb-4">
          <div className="modern-card p-4 flex-grow-1">
            <h4 className="d-flex align-items-center gap-2 mb-2">
              <FiBox color={iconColor} size={22} /> Configuração de Região
            </h4>
            <p className="mb-0" style={{ color: "#64748b" }}>
              Organize suas regiões com um fluxo de edição padrão e consistente.
            </p>
          </div>

          <div className="modern-card p-4" style={{ minWidth: 210 }}>
            <div className="d-flex align-items-center gap-2">
              <FiList color={iconColor} size={22} />
              <div>
                <div style={{ color: "#64748b", fontSize: 13 }}>Total de Regiões</div>
                <strong style={{ fontSize: 28 }}>{data.length}</strong>
              </div>
            </div>
          </div>
        </div>

        <motion.div className="modern-card p-4 mb-4" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <h5 className="mb-3 d-flex align-items-center gap-2">
            <FiPlusCircle color={iconColor} /> {editId ? "Editar Região" : "Nova Região"}
          </h5>

          <form onSubmit={handleCreateOrUpdate}>
            <div className="row align-items-end g-3">
              <div className="col-md-6 col-lg-4">
                <label className="form-label">Nome da Região</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Digite o nome da região"
                  value={formRegiao.name}
                  onChange={(e) => setFormRegiao({ name: e.target.value })}
                />
              </div>

              <div className="col-md-6 col-lg-4 d-flex gap-2">
                <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} type="submit" className="btn modern-primary-btn flex-fill">
                  <FiCheckCircle className="me-1" /> {editId ? "Salvar" : "Cadastrar"}
                </motion.button>

                {editId && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={handleCancelEdit}
                  >
                    <FiX className="me-1" /> Cancelar
                  </motion.button>
                )}
              </div>
            </div>
          </form>
        </motion.div>

        <div className="mb-3">
          <div className="input-group" style={{ maxWidth: "320px" }}>
            <span className="input-group-text bg-white">
              <FiSearch color={iconColor} />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Pesquisar região..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
            />
          </div>
        </div>

        <div className="modern-card p-2 p-md-3">
          <DataTable
            columns={columns}
            data={filteredItems}
            progressPending={pending}
            pagination
            highlightOnHover
            pointerOnHover
            responsive
            striped
            dense
            noHeader
          />
        </div>
      </motion.div>
    </LayoutSubmasterToUser>
  );
}

export default RegionFormSubmaster;
